/* eslint-disable curly */
const BASE = "/api";

export type CanvasType = "canvas" | "notebook";

export type NotebookPageStyle = "blank" | "ruled" | "grid" | "dotted";

export interface Folder {
  id: string;
  parent_id: string | null;
  name: string;
  position: number;
  created_at: number;
  updated_at: number;
}

export interface CanvasMeta {
  id: string;
  folder_id: string | null;
  name: string;
  description: string | null;
  position: number;
  created_at: number;
  updated_at: number;
  tags: string[];
  canvas_type: CanvasType;
  page_style: NotebookPageStyle | null;
  // Present only for canvases shared WITH me by another account (collaborator
  // access). `shared` flags them; `owner_username` is who owns the board.
  shared?: boolean;
  owner_username?: string;
}

export interface Collaborator {
  user_id: string;
  username: string;
  role: string;
}

export interface CanvasScene {
  type: string;
  version: number;
  source: string;
  elements: unknown[];
  appState: Record<string, unknown>;
  files: Record<string, unknown>;
}

export interface Tag {
  id: string;
  name: string;
  color: string | null;
}

export interface SharePayload {
  token: string;
  name: string | null;
  description: string | null;
  created_at: number;
}

export interface SharedScene {
  token: string;
  name: string | null;
  description: string | null;
  created_at: number;
  view_count: number;
  // 1 when guests may edit this board via the link; the canvas it's tied to.
  can_edit?: number;
  canvas_id?: string | null;
  canvas_type?: CanvasType | null;
  page_style?: NotebookPageStyle | null;
  scene: CanvasScene;
}

// --- Canvas version history -------------------------------------------------
// Inherently online (blobs live server-side): these calls do NOT go through
// libraryStore's offline op-log — the UI shows an "offline" empty state instead.
export type CanvasVersionKind = "auto" | "named" | "restore";

export interface CanvasVersionMeta {
  id: string;
  kind: CanvasVersionKind;
  label: string | null;
  author_name: string | null;
  created_at: number;
  byte_size: number;
}

export interface AuthUser {
  id: string;
  username: string;
  role: "admin" | "user";
}

export interface AuthStatus {
  needsSetup: boolean;
  authenticated: boolean;
  user: AuthUser | null;
}

export interface ManagedUser {
  id: string;
  username: string;
  role: "admin" | "user";
  created_at: number;
}

export interface BackupBundle {
  version: number;
  app: string;
  exportedAt: number;
  folders: {
    id: string;
    parent_id: string | null;
    name: string;
    position: number;
  }[];
  canvases: {
    id: string;
    folder_id: string | null;
    name: string;
    description: string | null;
    position: number;
    scene: CanvasScene;
    canvas_type: CanvasType;
    page_style: NotebookPageStyle | null;
  }[];
  tags: { id: string; name: string; color: string | null }[];
  canvasTags: { canvas_id: string; tag_id: string }[];
}

async function req<T>(url: string, init?: RequestInit): Promise<T> {
  const r = await fetch(BASE + url, {
    ...init,
    // Send the session cookie (same-origin in dev via Vite proxy and in prod).
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers || {}),
    },
  });
  if (!r.ok) {
    let detail = "";
    try {
      detail = await r.text();
    } catch {
      /* ignore */
    }
    throw new Error(`${r.status} ${r.statusText}: ${detail}`);
  }
  if (r.status === 204) return undefined as T;
  return r.json();
}

export const libraryApi = {
  listFolders: () => req<Folder[]>("/folders"),
  createFolder: (body: {
    name: string;
    parent_id?: string | null;
    id?: string;
    position?: number;
  }) => req<Folder>("/folders", { method: "POST", body: JSON.stringify(body) }),
  updateFolder: (
    id: string,
    body: Partial<Pick<Folder, "name" | "parent_id" | "position">>,
  ) =>
    req<Folder>(`/folders/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteFolder: (id: string) =>
    req<void>(`/folders/${id}`, { method: "DELETE" }),

  listCanvases: (folder_id?: string | null) => {
    const q =
      folder_id === undefined
        ? ""
        : `?folder_id=${folder_id === null ? "null" : folder_id}`;
    return req<CanvasMeta[]>(`/canvases${q}`);
  },
  getCanvas: (id: string) => req<CanvasMeta>(`/canvases/${id}`),
  createCanvas: (body: {
    name: string;
    folder_id?: string | null;
    scene?: unknown;
    id?: string;
    position?: number;
    canvas_type?: CanvasType;
    page_style?: NotebookPageStyle;
  }) =>
    req<CanvasMeta>("/canvases", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  // Atomic "get-or-create the default board" — converges multiple devices onto a
  // single board when they all lost their last one (see server route).
  ensureDefaultCanvas: (name: string, excludeId?: string | null) =>
    req<CanvasMeta>("/canvases/ensure-default", {
      method: "POST",
      body: JSON.stringify({ name, excludeId: excludeId ?? null }),
    }),
  updateCanvas: (
    id: string,
    body: Partial<{
      name: string;
      folder_id: string | null;
      position: number;
      description: string | null;
      canvas_type: CanvasType;
      page_style: NotebookPageStyle | null;
    }>,
  ) =>
    req<CanvasMeta>(`/canvases/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteCanvas: (id: string) =>
    req<void>(`/canvases/${id}`, { method: "DELETE" }),
  getScene: (id: string) => req<CanvasScene>(`/canvases/${id}/scene`),
  saveScene: (id: string, scene: unknown) =>
    req<{ ok: boolean; updated_at: number }>(`/canvases/${id}/scene`, {
      method: "PUT",
      body: JSON.stringify(scene),
    }),

  // --- Version history (online-only; see CanvasVersionMeta note) ---
  listVersions: (canvasId: string) =>
    req<CanvasVersionMeta[]>(`/canvases/${canvasId}/versions`),
  getVersionScene: (canvasId: string, versionId: string) =>
    req<CanvasScene>(`/canvases/${canvasId}/versions/${versionId}`),
  createNamedVersion: (canvasId: string, label: string) =>
    req<CanvasVersionMeta>(`/canvases/${canvasId}/versions`, {
      method: "POST",
      body: JSON.stringify({ label }),
    }),
  restoreVersion: (canvasId: string, versionId: string) =>
    req<{ ok: true; updated_at: number }>(
      `/canvases/${canvasId}/versions/${versionId}/restore`,
      { method: "POST" },
    ),
  deleteVersion: (canvasId: string, versionId: string) =>
    req<void>(`/canvases/${canvasId}/versions/${versionId}`, {
      method: "DELETE",
    }),

  listTags: () => req<Tag[]>("/tags"),
  createTag: (body: { name: string; color?: string | null }) =>
    req<Tag>("/tags", { method: "POST", body: JSON.stringify(body) }),
  deleteTag: (id: string) => req<void>(`/tags/${id}`, { method: "DELETE" }),
  addTagToCanvas: (canvasId: string, tagId: string) =>
    req<void>(`/canvases/${canvasId}/tags`, {
      method: "POST",
      body: JSON.stringify({ tag_id: tagId }),
    }),
  removeTagFromCanvas: (canvasId: string, tagId: string) =>
    req<void>(`/canvases/${canvasId}/tags/${tagId}`, { method: "DELETE" }),

  // --- Collaborators (account-to-account shared editing) ---
  // Typeahead for the share picker: empty q → first few users; otherwise matches.
  searchUsers: (q: string) =>
    req<{ id: string; username: string }[]>(
      `/users/search?q=${encodeURIComponent(q)}`,
    ),
  listCollaborators: (canvasId: string) =>
    req<{ owner_username: string | null; collaborators: Collaborator[] }>(
      `/canvases/${canvasId}/collaborators`,
    ),
  addCollaborator: (canvasId: string, username: string) =>
    req<Collaborator>(`/canvases/${canvasId}/collaborators`, {
      method: "POST",
      body: JSON.stringify({ username }),
    }),
  removeCollaborator: (canvasId: string, userId: string) =>
    req<void>(`/canvases/${canvasId}/collaborators/${userId}`, {
      method: "DELETE",
    }),

  createShare: (body: {
    scene: unknown;
    name?: string | null;
    canvas_id?: string | null;
    description?: string | null;
    can_edit?: boolean;
  }) =>
    req<SharePayload & { can_edit?: number }>("/shares", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  getShare: (token: string) => req<SharedScene>(`/shares/${token}`),
  getShareForCanvas: (canvasId: string) =>
    req<{
      token: string;
      name: string | null;
      description: string | null;
      created_at: number;
      view_count: number;
      can_edit?: number;
    }>(`/shares/by-canvas/${canvasId}`),
  // Toggle whether guests can edit via the link (owner only).
  updateShare: (token: string, body: { can_edit: boolean }) =>
    req<{ token: string; can_edit: number }>(`/shares/${token}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  // Guest write-back to an editable share (public; server checks can_edit).
  saveSharedScene: (token: string, scene: unknown) =>
    req<{ ok: boolean; updated_at: number }>(`/shares/${token}/scene`, {
      method: "PUT",
      body: JSON.stringify(scene),
    }),
  revokeShare: (token: string) =>
    req<void>(`/shares/${token}`, { method: "DELETE" }),

  // --- Auth ---
  authStatus: () => req<AuthStatus>("/auth/status"),
  setup: (body: { username: string; password: string }) =>
    req<{ user: AuthUser }>("/auth/setup", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  login: (body: { username: string; password: string }) =>
    req<{ user: AuthUser }>("/auth/login", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  logout: () => req<void>("/auth/logout", { method: "POST" }),

  // --- User management (admin) ---
  listUsers: () => req<ManagedUser[]>("/users"),
  createUser: (body: {
    username: string;
    password: string;
    role: "admin" | "user";
  }) =>
    req<ManagedUser>("/users", { method: "POST", body: JSON.stringify(body) }),
  updateUser: (
    id: string,
    body: { password?: string; role?: "admin" | "user" },
  ) =>
    req<ManagedUser>(`/users/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteUser: (id: string) => req<void>(`/users/${id}`, { method: "DELETE" }),

  // --- Backup / restore ---
  exportBackup: () => req<BackupBundle>("/backup"),
  importBackup: (bundle: BackupBundle) =>
    req<{ folders: number; canvases: number }>("/backup/import", {
      method: "POST",
      body: JSON.stringify(bundle),
    }),
};
